// CC-055 — Call Prep checklist for the stage owner. Reads never generate a list.
import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const errText = (e) => e?.response?.data?.detail || e.message;

function Item({ it, onToggle, busy }) {
  return (
    <li className="flex gap-3 py-2 border-b border-[#f1f2f4] last:border-0" data-testid={`call-prep-item-${it.id}`}>
      <input type="checkbox" className="mt-1" checked={!!it.checked} disabled={busy}
             onChange={(e) => onToggle(it, e.target.checked)} data-testid={`call-prep-check-${it.id}`} />
      <div className="text-sm text-[#0e1420]">
        <div className={it.checked ? "line-through text-[#6b7280]" : ""} data-testid={`call-prep-text-${it.id}`}>
          {it.text}
          {it.compliance && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#1c6a99]"
                  data-testid={`call-prep-compliance-${it.id}`}><ShieldCheck className="w-3 h-3" />Read as written</span>
          )}
        </div>
        {it.script && <div className="text-xs italic text-[#4b5563] mt-0.5" data-testid={`call-prep-script-${it.id}`}>Say: "{it.script}"</div>}
        {it.why && <div className="text-xs text-[#4b5563] mt-0.5" data-testid={`call-prep-why-${it.id}`}>Why: {it.why}</div>}
      </div>
    </li>
  );
}

function Body({ prep, canGenerate, onGenerate, busy, onToggle }) {
  if (!prep || prep.status === "failed") {
    return (
      <div className="text-sm text-[#4b5563]" data-testid="call-prep-empty">
        {prep ? `The list couldn't be prepared (${prep.error || "error"}).` : "No call prep list for this candidate yet."}
        {canGenerate && (
          <button className="ml-2 text-[#0072bc] hover:underline disabled:opacity-50" disabled={busy}
                  onClick={onGenerate} data-testid="call-prep-generate-btn">{prep ? "Try again" : "Generate list"}</button>
        )}
      </div>
    );
  }
  if (prep.status === "generating") {
    return <div className="text-sm text-[#4b5563] flex items-center gap-2" data-testid="call-prep-generating"><Loader2 className="w-4 h-4 animate-spin" />Preparing the list…</div>;
  }
  const base = prep.items.filter((i) => i.kind === "baseline");
  const extra = prep.items.filter((i) => i.kind === "candidate");
  return (
    <div className="space-y-3">
      {prep.note && <div className="text-xs font-medium text-[#7c2d12] bg-[#fff7ed] border border-[#fed7aa] rounded px-2 py-1.5" data-testid="call-prep-note">{prep.note}</div>}
      {prep.items.length === 0 && <div className="text-sm text-[#4b5563]" data-testid="call-prep-no-items">No questions are set for this stage.</div>}
      {extra.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[#4b5563] mb-1">For this candidate</div>
          <ul data-testid="call-prep-candidate-list">{extra.map((it) => <Item key={it.id} it={it} onToggle={onToggle} busy={busy} />)}</ul>
        </div>
      )}
      <ul data-testid="call-prep-baseline-list">{base.map((it) => <Item key={it.id} it={it} onToggle={onToggle} busy={busy} />)}</ul>
      {prep.dropped?.length > 0 && (
        <div className="text-xs text-[#6b7280]" data-testid="call-prep-dropped">
          Skipped (already answered in the application): {prep.dropped.map((d) => d.text).join(" · ")}
        </div>
      )}
    </div>
  );
}

export default function CallPrepPanel({ candidateId, canGenerate }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const base = `/hiring/candidates/${candidateId}/call-prep`;
  const load = useCallback(() => api.get(base).then(({ data: d }) => setData(d)).catch(() => setData({ visible: false })), [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.prep?.status !== "generating") return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data, load]);
  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { const { data: d } = await fn(); setData(d); } catch (e) { setErr(String(errText(e))); } finally { setBusy(false); }
  };
  if (!data?.visible) return null;
  return (
    <section className="bg-white border border-[#e5e7eb] rounded-lg p-4" data-testid="call-prep-panel">
      <h2 className="flex items-center gap-2 text-base font-semibold text-[#0e1420] mb-3">
        <ClipboardCheck className="w-4 h-4 text-[#0072bc]" /> Call Prep · Stage {data.stage}
      </h2>
      <Body prep={data.prep} canGenerate={canGenerate} busy={busy}
            onGenerate={() => run(() => api.post(`${base}/generate`, {}))}
            onToggle={(it, checked) => run(() => api.post(`${base}/items/${it.id}/check`, { checked }))} />
      {err && <div className="text-xs text-[#991b1b] mt-2" data-testid="call-prep-error">{err}</div>}
    </section>
  );
}
